/**
 * Typographie, graisses et couleurs communes aux graphiques Plotly Homeys.
 *
 * Aligné sur `homeys_kit/graph/constants/template_graph.json` → `light.layout`
 * (`axes`, `legend`, `tooltip`, `figure`). Plotly attend `font.weight` en nombre (100–900) :
 * équivalents `bold` → 700, `normal` → 400.
 */

export const HOMEYS_PLOTLY_FONT_FAMILY = "Arial"

/** `light.layout.axes.tick.font.color` & titres / légende. */
export const HOMEYS_PLOTLY_TEXT_COLOR = "#2D2E32"

/** `light.layout.tooltip.font.color`. */
export const HOMEYS_PLOTLY_HOVER_TEXT_COLOR = "#000000"

/** Équivalent `weight: "bold"` du kit. */
export const HOMEYS_PLOTLY_FONT_WEIGHT_BOLD = 700

/** Équivalent `weight: "normal"` du kit (tooltip). */
export const HOMEYS_PLOTLY_FONT_WEIGHT_NORMAL = 400

/** `light.layout.axes.tick.font` (+ `newshape_legendgrouptitle.font`, `legend.font`). */
export const HOMEYS_PLOTLY_AXIS_TICK_FONT = {
    family: HOMEYS_PLOTLY_FONT_FAMILY,
    size: 10,
    color: HOMEYS_PLOTLY_TEXT_COLOR,
    weight: HOMEYS_PLOTLY_FONT_WEIGHT_BOLD,
} as const

/** `light.layout.axes.title.font`. */
export const HOMEYS_PLOTLY_AXIS_TITLE_FONT = {
    family: HOMEYS_PLOTLY_FONT_FAMILY,
    size: 12,
    color: HOMEYS_PLOTLY_TEXT_COLOR,
    weight: HOMEYS_PLOTLY_FONT_WEIGHT_BOLD,
} as const

/** `light.layout.legend.font`. */
export const HOMEYS_PLOTLY_LEGEND_FONT = {
    family: HOMEYS_PLOTLY_FONT_FAMILY,
    size: 10,
    color: HOMEYS_PLOTLY_TEXT_COLOR,
    weight: HOMEYS_PLOTLY_FONT_WEIGHT_BOLD,
} as const

/** `light.layout.tooltip.font` (hoverlabel Plotly). */
export const HOMEYS_PLOTLY_HOVERLABEL_FONT = {
    family: HOMEYS_PLOTLY_FONT_FAMILY,
    size: 12,
    color: HOMEYS_PLOTLY_HOVER_TEXT_COLOR,
    weight: HOMEYS_PLOTLY_FONT_WEIGHT_NORMAL,
} as const

/** `light.layout.axes.color` (lignes / ticks). */
export const HOMEYS_PLOTLY_AXES_LINE_COLOR = "#ACAEB2"

/** `light.layout.axes.yaxis.grid.color`. */
export const HOMEYS_PLOTLY_GRID_COLOR = "#ECECEC"

/** `light.layout.figure.background`. */
export const HOMEYS_PLOTLY_FIGURE_BACKGROUND = "#fff"

/** `light.layout.legend.background`. */
export const HOMEYS_PLOTLY_LEGEND_BACKGROUND = "#fff"

/** `light.layout.tooltip.background`. */
export const HOMEYS_PLOTLY_TOOLTIP_BACKGROUND = "#FFFFFF"

/** `light.layout.tooltip.bordercolor`. */
export const HOMEYS_PLOTLY_TOOLTIP_BORDER_COLOR = "#ACAEB2"

/** `light.layout.axes.tick.marker.length`. */
export const HOMEYS_PLOTLY_TICK_LENGTH = 10

/**
 * La PALETTE du kit : `light.layout.colorway` de
 * `homeys_kit/graph/constants/template_graph.json`, recopiée hex par hex.
 *
 * Elle n'est POSÉE nulle part par défaut : ni dans le fond de survol partagé,
 * ni dans l'enrichissement d'axes. Les figures du courtage et du photovoltaïque
 * laissent Plotly choisir ses couleurs, et les leur changer ici fabriquerait un
 * rendu de plus. Seul un appelant qui la demande explicitement la reçoit.
 */
export const HOMEYS_PLOTLY_COLORWAY = [
    "#35C4DC",
    "#6586D5",
    "#EAB308",
    "#EC4899",
    "#22C55E",
    "#6366F1",
    "#F97316",
    "#64748B",
    "#14B8A6",
    "#FF3D32",
    "#A855F7",
    "#9E9E9E",
] as const

/** `light.layout.axes.tick.marker.width`. */
export const HOMEYS_PLOTLY_TICK_WIDTH = 1

/**
 * Marges du kit (`light.layout.figure.margin`). Le layout enrichi peut augmenter `b`
 * pour laisser la place à la légende Plotly sous l’axe.
 */
export const HOMEYS_PLOTLY_FIGURE_MARGIN_KIT = {
    l: 80,
    r: 40,
    t: 20,
    b: 0,
} as const

/**
 * Fusionne la typo des ticks avec les surcharges éventuelles (`partial.tickfont`).
 */
export function mergeHomeysPlotlyAxisTickFont(
    axis: Record<string, unknown>
): Record<string, unknown> {
    const prev =
        axis.tickfont && typeof axis.tickfont === "object"
            ? (axis.tickfont as Record<string, unknown>)
            : {}
    return {
        ...axis,
        tickfont: { ...HOMEYS_PLOTLY_AXIS_TICK_FONT, ...prev },
    }
}
